import { useCallback } from 'react'
import { Archive, Link2Off } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { ParentTaskIndicatorProps } from '../type'
import { TaskStatusCheckbox } from '@/components/app/tasks/TaskStatusCheckbox'
import { useTaskMutations } from '@/lib/db/rxdb/hooks'
import { useProjectStatusMode } from '@/hooks/useProjectStatusMode'
import { cn } from '@/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'

export function ParentTaskIndicator({
  parentTask,
  statuses: _statuses,
  className,
  onParentTaskClick,
  onUnlink,
}: ParentTaskIndicatorProps) {
  const { t } = useTranslation(['tasks', 'common'])
  const unlinkLabel = t('actions.unlinkSubtask', {
    defaultValue: 'Unlink sub-task',
  })
  const unlinkTitle = t('dialogs.unlinkSubtask.title', {
    defaultValue: 'Unlink sub-task?',
  })
  const unlinkDescription = t('dialogs.unlinkSubtask.description', {
    defaultValue:
      'This removes the sub-task from this parent. The task will not be deleted.',
  })
  const navigate = useNavigate()
  const { updateTask } = useTaskMutations()
  // Resolve mode from the parent's own project so cross-project parents are correct
  const { statusMode: parentStatusMode } = useProjectStatusMode(
    parentTask.projectId,
  )

  const handleStatusChange = useCallback(
    async (statusId: string) => {
      if (!statusId || parentTask.statusId === statusId) {
        return
      }

      try {
        await updateTask(parentTask.id, {
          statusId,
        })
      } catch (error) {
        // Error handling
      }
    },
    [parentTask?.id, parentTask?.statusId, updateTask],
  )

  const handleNameClick = useCallback(() => {
    // If a callback is provided, use it instead of navigation
    if (onParentTaskClick) {
      onParentTaskClick(parentTask.id)
      return
    }

    // Otherwise, fall back to navigation
    navigate({
      to: '/$workspaceSlug/tasks/$taskId',
      params: {
        taskId: parentTask.id,
        workspaceSlug: window.location.pathname.split('/')[1] ?? '', // Extract workspace slug from current path
      },
    })
  }, [parentTask.id, onParentTaskClick, navigate])

  return (
    <>
      <div
        className={cn('group flex items-center gap-2 text-sm text-muted-foreground', className)}
      >
        <span className="shrink-0">Sub-task of</span>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <TaskStatusCheckbox
            currentStatusId={parentTask.statusId}
            onStatusChange={handleStatusChange}
            statusMode={parentStatusMode}
            className="h-5 w-5"
          />
          <button
            type="button"
            onClick={handleNameClick}
            className={cn(
              'hover:text-blue-600 dark:hover:text-blue-400 hover:underline transition-colors font-medium truncate',
              parentTask.isArchived
                ? 'text-muted-foreground line-through'
                : 'text-foreground',
            )}
          >
            {parentTask.name || 'Untitled task'}
          </button>
          {parentTask.isArchived && (
            <span
              className="inline-flex items-center gap-1 shrink-0 rounded-full border-transparent bg-secondary text-secondary-foreground px-2 py-0.5 text-[10px] font-medium"
              title="Archived"
            >
              <Archive className="size-3" />
              Archived
            </span>
          )}
        </div>
        {onUnlink && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                onClick={(event) => event.stopPropagation()}
                className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition hover:bg-accent hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary group-hover:opacity-100"
                aria-label={unlinkLabel}
                title={unlinkLabel}
              >
                <Link2Off className="size-4" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent onClick={(event) => event.stopPropagation()}>
              <AlertDialogHeader>
                <AlertDialogTitle>{unlinkTitle}</AlertDialogTitle>
                <AlertDialogDescription>
                  {unlinkDescription}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>
                  {t('common:actions.cancel')}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={(event) => {
                    event.stopPropagation()
                    onUnlink()
                  }}
                >
                  {unlinkLabel}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </>
  )
}
